/**
 * design/27-ui-surface-and-navigation.md §27.1 (the six groups, closed) and §27.2 (the route
 * register). Hand-transcribed from those two tables — nothing here originates a value; where the
 * design table and this file ever disagree, the design table wins and this file is the defect.
 *
 * `LLD-SPA-023`: "the groups and their leaves come from the register with a fixed order. Adding
 * one is a register change that regenerates the route table" — this file is that register.
 * `LLD-SPA-024`: "each screen's description lives once and is rendered by both the home index and
 * its group landing page" — `NavLeaf.summary` is that one description.
 *
 * `unitId` links each leaf to `process/03-SCREEN-INVENTORY.md` §0's register — `undefined` for
 * the two entries §27.2 states are "not nav destinations" (`blocked`, reached by state; and the
 * report drill-downs, one templated route rather than one leaf each — `LLD-SPA-029`).
 */
import { unitId } from '@/annotations'
import type { UnitId } from '@/annotations'
import type { IconName } from '@/components/icons/iconData'
import type { LauncherCardVariant } from '@/components/LauncherCard'

export interface NavLeaf {
  readonly routeId: string
  readonly title: string
  readonly unitId: UnitId
  readonly authority: string
  /** The one description of this screen (LLD-SPA-024) — rendered by both the home index
   * (`VHome`) and its own group landing page (`GroupLanding`), never duplicated. Sourced from
   * Prototype_v2.html's own `v-home` card text for each leaf that still has one there verbatim
   * (`design/27-ui-surface-and-navigation.md` §27.1 adopts the prototype's surface in full); the
   * three leaves that don't (`credential` supersedes the prototype's `drconn` card per §27.4 #3,
   * and `connected`/`credential` carry §27.4 #4's and §19.10's own post-merge scope) are written
   * from those sections' own words rather than invented. */
  readonly summary: string
  readonly icon: IconName
}

export interface NavGroup {
  readonly routeId: string
  readonly title: string
  /** `div.sect` + `div.grid3`'s one colour for the whole group (`process/08-COMPONENT-CATALOGUE.md`
   * `launcher-card` variants) — the prototype colours every leaf in a group identically. */
  readonly accent: LauncherCardVariant
  /** `Sidebar.tsx`'s own `nav-group` row icon (W175) — the prototype's own `.nav-item.top`, one
   * filled (`f-*`) icon per group, the same colour as `accent`. */
  readonly icon: IconName
  readonly leaves: readonly NavLeaf[]
}

// `unitId()` both brands each value AND checks it against the generated register at module-load
// time — a typo here fails the build immediately rather than surfacing as a silent dead route.

export const HOME: { readonly routeId: 'home'; readonly title: string; readonly icon: IconName } = {
  routeId: 'home',
  title: 'Bitbucket Command Center',
  icon: 'f-home',
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    routeId: 'secdsc',
    title: 'Data Security and Compliance',
    accent: 'green',
    icon: 'f-shield',
    leaves: [
      {
        routeId: 'dashboard',
        title: 'Admin Dashboard',
        unitId: unitId('v-dashboard'),
        authority: '§27.3',
        icon: 'i-chart',
        summary: 'Get a high-level overview of your Bitbucket workspace protection, storage and retention.',
      },
      {
        routeId: 'backup',
        title: 'Backup',
        unitId: unitId('v-backup'),
        authority: '§4, §4c, §5',
        icon: 'i-backup',
        summary: 'Start a full or partial backup of a Bitbucket workspace instantly, at any time, completely outside of the regular schedule.',
      },
      {
        routeId: 'restore',
        title: 'Restore',
        unitId: unitId('v-restore'),
        authority: '§18.3',
        icon: 'i-restore',
        summary:
          'Restore a single component, a repository, a project or everything, into the same workspace or a different one, after a summary of what the restore will attempt.',
      },
      {
        routeId: 'audit',
        title: 'Audit Logs',
        unitId: unitId('v-audit'),
        authority: '§9 audit rule',
        icon: 'i-doc',
        summary: 'Read and export the record of who initiated which operation in this product, with the parameters supplied and the result.',
      },
    ],
  },
  {
    routeId: 'seccore',
    title: 'Core Administration',
    accent: 'teal',
    icon: 'f-sliders',
    leaves: [
      {
        routeId: 'jobs',
        title: 'Jobs',
        unitId: unitId('v-jobs'),
        authority: '§4, §4a',
        icon: 'i-list',
        summary: 'View and manage the real-time status of every active, scheduled and completed backup and restore job, and read its log.',
      },
      {
        routeId: 'connected',
        title: 'Connected Workspaces',
        unitId: unitId('v-connected'),
        authority: '§18.8, §24.2b G2, §27.4 #4',
        icon: 'i-inout',
        summary:
          'Manage and authenticate your connections to other Bitbucket Cloud workspaces, for cross-workspace restore, repository migration and disaster recovery.',
      },
    ],
  },
  {
    routeId: 'secanalytics',
    title: 'Analytics and Insights',
    accent: 'indigo',
    icon: 'f-chart',
    leaves: [
      {
        routeId: 'drift',
        title: 'Configuration Drift',
        unitId: unitId('v-drift'),
        authority: '§23',
        icon: 'i-drift',
        summary: 'Compare your workspace configuration between two configuration snapshots to identify and report on any unintended changes.',
      },
    ],
  },
  {
    routeId: 'secreporting',
    title: 'Reporting',
    accent: 'magenta',
    icon: 'f-rocket',
    leaves: [
      {
        routeId: 'health',
        title: 'Workspace Health',
        unitId: unitId('v-health'),
        authority: '§28',
        icon: 'i-shield',
        summary:
          'Generate reports on access, branch protection, repository activity, pipelines, storage, file types and seats, graded against our published baseline.',
      },
    ],
  },
  {
    routeId: 'secsettings',
    title: 'Settings',
    accent: 'orange',
    icon: 'f-gear',
    leaves: [
      {
        routeId: 'setbackup',
        title: 'Backup Settings',
        unitId: unitId('v-setbackup'),
        authority: '§12.5, §14',
        icon: 'i-backup',
        summary:
          'Configure all aspects of your backup strategy, including exactly what to back up, your two schedules, retention, storage target and Bitbucket credentials.',
      },
      {
        routeId: 'notify',
        title: 'Notifications',
        unitId: unitId('v-notify'),
        authority: '§20',
        icon: 'i-alert',
        summary: 'Choose the email addresses that receive alerts, and which events they are sent for, such as failed jobs and deletion alarms.',
      },
      {
        routeId: 'credential',
        title: 'Bitbucket Credential',
        unitId: unitId('v-credential'),
        authority: '§12.7, §19.10, §27.4 #3',
        icon: 'i-key',
        summary: 'Manage the Bitbucket API token this workspace authenticates with — its type, scopes and expiry — and replace it without ever exposing its value.',
      },
    ],
  },
  {
    routeId: 'secaccount',
    title: 'Account',
    accent: 'indigo',
    icon: 'f-board',
    leaves: [
      {
        routeId: 'users',
        title: 'Users and Roles',
        unitId: unitId('v-users'),
        authority: '§3.1b',
        icon: 'i-users',
        summary: 'Invite the people who use this product, set what each of them may do, and see which workspaces each one can reach.',
      },
    ],
  },
]

export function findLeaf(routeId: string): { group: NavGroup; leaf: NavLeaf } | undefined {
  for (const group of NAV_GROUPS) {
    const leaf = group.leaves.find((l) => l.routeId === routeId)
    if (leaf) return { group, leaf }
  }
  return undefined
}
